import React from 'react';
import { CircleDollarSignIcon } from 'lucide-react';
import { Card, CardHeader } from './Card';
import type { EstimatorColumn, LoanOfficer } from '../types/commission';
import { formatBps, formatCurrency, formatPercent } from '../utils/format';
interface ClosedToDateCardProps {
  officer: LoanOfficer;
  actuals: EstimatorColumn;
  monthLabel: string;
}
export function ClosedToDateCard({
  officer,
  actuals,
  monthLabel
}: ClosedToDateCardProps) {
  const items = [{
    label: 'Loans closed',
    value: `${actuals.units}`,
    helper: `Goal ${actuals.goalUnits}`
  }, {
    label: 'Funded volume',
    value: formatCurrency(actuals.volume)
  }, {
    label: 'Loan pay earned',
    value: formatCurrency(actuals.basePay),
    helper: `${formatBps(actuals.tier.basisPoints)} at ${actuals.tier.name}`
  }, {
    label: 'Unit bonus',
    value: formatCurrency(actuals.tierBonus),
    helper: `${formatCurrency(actuals.tier.unitBonus)} per loan`
  }, {
    label: 'Overages paid',
    value: formatCurrency(actuals.overageShare),
    helper: `${formatPercent(actuals.tier.overageSplit)} of ${formatCurrency(actuals.overageDollars)}`
  }];
  return <Card data-id="element-20" data-mp="2kwdmgk37nypy">
      <CardHeader title="Closed to date" description={`${monthLabel} — funded volume and commissions earned, overages included.`} icon={<CircleDollarSignIcon className="h-4 w-4" aria-hidden="true" data-id="element-22" data-mp="2fvg68d1rher3" />} action={<div className="text-right" data-id="element-23" data-mp="1s880ju0f83oj">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-24" data-mp="1o6muarie6nnb">
              Earned so far
            </p>
            <p className="text-2xl font-semibold tabular-nums text-emerald-700" data-id="element-25" data-mp="21yji4jd12bdw">
              {formatCurrency(actuals.totalPayout)}
            </p>
          </div>} data-id="element-21" data-mp="2kx5ghqh5toe4" />
      <dl className="grid grid-cols-2 divide-slate-100 sm:grid-cols-5 sm:divide-x" data-id="element-26" data-mp="19gozvttvd1po">
        {items.map(item => <div key={item.label} className="px-5 py-4" data-id="element-27" data-mp="14hcbh4ncnmkz">
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-28" data-mp="34kn016qietqe">
              {item.label}
            </dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums text-slate-900" data-id="element-29" data-mp="39ioipqk0wr1c">{item.value}</dd>
            {item.helper ? <dd className="mt-0.5 text-xs text-slate-500" data-id="element-30" data-mp="3eb87ljo5v8lg">{item.helper}</dd> : null}
          </div>)}
      </dl>
      <div className="border-t border-slate-100 px-5 py-3" data-id="element-31" data-mp="2llra47lop1id">
        <div className="flex items-center gap-3" data-id="element-32" data-mp="2lnc5u0jqiqht">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200" role="img" aria-label={`${formatPercent(actuals.goalAttainment)} of loan goal`} data-id="element-33" data-mp="2qkc8e1hdlb46">
            <div className="h-full rounded-full bg-emerald-500" style={{
            width: `${Math.min(100, actuals.goalAttainment * 100)}%`
          }} data-id="element-34" data-mp="2zlskqerr3nb1" />
          </div>
          <p className="text-xs font-medium tabular-nums text-slate-600" data-id="element-35" data-mp="eus2i31ikvrr">
            {formatPercent(actuals.goalAttainment)} of goal · {actuals.unitsToGoal} loan
            {actuals.unitsToGoal === 1 ? '' : 's'} to go for {officer.name.split(' ')[0]}
          </p>
        </div>
      </div>
    </Card>;
}